import { useEffect } from "react";

/**
 * Small UI helpers shared across pages.
 *
 * Pure where possible so wording rules are tested rather than eyeballed — "1 calls" is the kind
 * of thing that survives review and then reads as broken to an owner.
 */

/** "1 call" / "2 calls". Pass `plural` for irregular words. */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Set the browser tab title.
 *
 * Restaurants keep several tabs open — the order queue on a till, the menu on a laptop — and
 * every tab currently reads "Restaurant Console · glowin", which makes them indistinguishable.
 */
export function usePageTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · glowin`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * Start of today, local, as an ISO instant.
 *
 * The order queue only needs today's service: it used to fetch every order ever placed and poll
 * that every 15 seconds.
 */
export function startOfToday(now = new Date()): string {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** Start of a "YYYY-MM" month, local, as an ISO instant. */
export function startOfMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, 1, 0, 0, 0, 0).toISOString();
}

/** "YYYY-MM-DD" for a local date, the shape a <input type="date"> uses. */
export function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayInput(now = new Date()): string {
  return toDateInput(now);
}

/** The local day named by "YYYY-MM-DD", as a pair of ISO instants. */
export function dayWindow(dateInput: string): { since: string; until: string } {
  const [y, m, d] = dateInput.split("-").map(Number);
  const start = new Date(y!, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
  const end = new Date(y!, (m ?? 1) - 1, d ?? 1, 23, 59, 59, 999);
  return { since: start.toISOString(), until: end.toISOString() };
}

/** Shift a "YYYY-MM-DD" by whole days. */
export function shiftDay(dateInput: string, days: number): string {
  const [y, m, d] = dateInput.split("-").map(Number);
  const shifted = new Date(y!, (m ?? 1) - 1, (d ?? 1) + days);
  return toDateInput(shifted);
}

/** ISO instant N days before now, for catching orders still active from earlier days. */
export function daysAgo(days: number, now = new Date()): string {
  return new Date(now.getTime() - days * 86_400_000).toISOString();
}
